import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RoleBadge } from "@/components/badges";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AlertCircle, Building2, HardDrive, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function AcceptInvite() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/invite/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data?.name) setName(r.data.name);
      })
      .catch((e) => setLoadError(fmtErr(e, t("invite.invalidLink"))));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!info?.exists && password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name: name || undefined, password, language: i18n.language });
      setUser(data.user);
      navigate("/");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-2">
          <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="h-12 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</span>
          <div className="[margin-inline-start:auto]">
            <LanguageSwitcher />
          </div>
        </div>

        {loadError && (
          <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="invite-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {loadError}
          </div>
        )}

        {!loadError && !info && (
          <div className="flex items-center gap-2 text-slate-500" data-testid="invite-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> {t("invite.loading")}
          </div>
        )}

        {info && (
          <div data-testid="invite-card">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("invite.title")}</h2>
            <div className="mt-4 rounded-xl border bg-white p-5 shadow-sm">
              {info.kind === "employee" ? (
                <div className="flex items-start gap-3">
                  <HardDrive className="mt-0.5 h-5 w-5 text-slate-400" />
                  <div>
                    <div className="font-display text-base font-semibold text-slate-900" data-testid="invite-project-name">Pruva Drive</div>
                    <div className="mt-1 text-sm text-slate-600">
                      <span className="font-medium">{t("invite.driveDesc")}</span>
                    </div>
                    <div className="mt-3 font-mono text-xs text-slate-500" data-testid="invite-email">{info.email}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <Building2 className="mt-0.5 h-5 w-5 text-slate-400" />
                  <div>
                    <div className="font-display text-base font-semibold text-slate-900" data-testid="invite-project-name">{info.project_name}</div>
                    <div className="mt-1 text-sm text-slate-600">
                      <span className="font-medium">{info.company_name}</span>
                    </div>
                    <div className="mt-2"><RoleBadge role={info.role} data-testid="invite-role-badge" /></div>
                    <div className="mt-3 font-mono text-xs text-slate-500" data-testid="invite-email">{info.email}</div>
                    <p className="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="invite-plan-note">
                      {t("invite.planNote")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="accept-invite-form">
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="accept-invite-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}
              {info.exists ? (
                <div className="space-y-1.5">
                  <Label htmlFor="pw">{t("invite.existingPw")}</Label>
                  <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" />
                  <p className="text-xs text-slate-500">{t("invite.existingNote")}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="name">{t("invite.fullName")}</Label>
                    <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" placeholder="Jane Doe" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pw">{t("invite.setPassword")}</Label>
                    <Input id="pw" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pw2">{t("reset.confirmPassword")}</Label>
                    <Input id="pw2" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm-input" />
                  </div>
                </>
              )}
              {i18n.language !== "en" && (
                <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="invite-email-lang-note">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {t("invite.emailLangNote")}
                </p>
              )}
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="accept-invite-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {info.exists ? (info.kind === "employee" ? t("invite.confirmContinue") : t("invite.linkAccess")) : t("invite.createJoin")}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
